import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ShieldCheck, Scan, Sparkles, CheckCircle2, AlertTriangle, History, RefreshCw,
  Flag, X, Wand2, Printer, Loader2, Eye, FileDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authCfg = () => {
  const token = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${token}` } };
};

const riskColor = (r) => r === 'HIGH'
  ? 'bg-rose-100 text-rose-800 border-rose-300'
  : r === 'MEDIUM'
  ? 'bg-amber-100 text-amber-800 border-amber-300'
  : 'bg-emerald-100 text-emerald-800 border-emerald-300';

const ContentAuthenticityAuditor = () => {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [termCount, setTermCount] = useState(0);
  const [activeItem, setActiveItem] = useState(null);     // item for candidate gen
  const [prompt, setPrompt] = useState('');
  const [sceneKey, setSceneKey] = useState('');
  const [sceneLabel, setSceneLabel] = useState('');
  const [candidate, setCandidate] = useState(null);       // {candidate_url, bytes}
  const [generating, setGenerating] = useState(false);
  const [approvePw, setApprovePw] = useState('');
  const [approving, setApproving] = useState(false);
  const [history, setHistory] = useState([]);
  const [pressStatus, setPressStatus] = useState(null);
  const [pressRegenStarting, setPressRegenStarting] = useState(false);
  const [forceRegenAll, setForceRegenAll] = useState(false);  // v6.32.138 — exposes force_regen_all to the UI so operators can re-bake existing GridFS PDFs (e.g. to apply the v6.32.136 parity watermark).

  const runScan = useCallback(async () => {
    setScanning(true);
    try {
      const { data } = await axios.get(`${API}/content-auditor/scan`, authCfg());
      setScan(data);
    } catch (e) {
      toast.error(`Scan failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setScanning(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content-auditor/config`, authCfg());
      setTermCount(data.term_count || 0);
    } catch (_) {}
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content-auditor/history`, authCfg());
      setHistory(data.manifests || []);
    } catch (_) {}
  }, []);

  const loadPressStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content-auditor/regen-press-status`, authCfg());
      setPressStatus(data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([runScan(), loadConfig(), loadHistory(), loadPressStatus()])
      .finally(() => setLoading(false));
  }, [runScan, loadConfig, loadHistory, loadPressStatus]);

  const beginGenerate = (item) => {
    const key = (item.trigger || 'scene').toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40);
    setSceneKey(`${key}_${item.grade_level || 'g'}_${(item.modality || 'm').slice(0, 2)}`);
    setSceneLabel(`${item.trigger || 'Scene'} · Grade ${item.grade_level || '?'} · ${item.modality || ''}`);
    setPrompt(
      `A warm, photographic-style, text-free illustration for a K-12 WIDA assessment item.\n\n` +
      `CULTURAL CONTEXT: ${item.trigger || 'cultural scene'}\n` +
      `GRADE: ${item.grade_level || '?'}  · MODALITY: ${item.modality || '?'}\n\n` +
      `STIMULUS TO ILLUSTRATE:\n${item.stimulus || item.big_picture_context || ''}\n\n` +
      `QUESTION THE IMAGE MUST SUPPORT:\n"${item.question_text || ''}"\n\n` +
      `HARD RULES:\n` +
      `- Absolutely NO text anywhere: no letters, no words, no signage.\n` +
      `- Respectful, authentic cultural representation.\n` +
      `- Age-appropriate for the listed grade.\n` +
      `- 4:3 aspect ratio.`
    );
    setCandidate(null);
    setApprovePw('');
    setActiveItem(item);
  };

  const doGenerate = async () => {
    if (prompt.length < 40) {
      toast.error('Prompt must be at least 40 characters');
      return;
    }
    setGenerating(true);
    setCandidate(null);
    try {
      const { data } = await axios.post(`${API}/content-auditor/generate`,
        { scene_key: sceneKey, scene_label: sceneLabel, prompt }, authCfg());
      setCandidate(data);
      toast.success(`Generated (${Math.round(data.bytes / 1024)} KB)`);
    } catch (e) {
      toast.error(`Generate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const doApprove = async (ids) => {
    if (!approvePw) {
      toast.error('Password required');
      return;
    }
    if (!candidate?.candidate_url) {
      toast.error('Generate a candidate image first');
      return;
    }
    setApproving(true);
    try {
      const { data } = await axios.post(`${API}/content-auditor/approve`, {
        scene_key: sceneKey,
        question_ids: ids,
        candidate_url: candidate.candidate_url,
        password: approvePw,
        note: sceneLabel,
      }, authCfg());
      toast.success(`Approved · ${data.question_count} item(s) updated · ${data.manifest_id}`);
      setActiveItem(null);
      setCandidate(null);
      setApprovePw('');
      await Promise.all([runScan(), loadHistory()]);
    } catch (e) {
      toast.error(`Approve failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setApproving(false);
    }
  };

  const doManualFlag = async (question_id) => {
    const reason = prompt2('Why is this item flagged?');
    if (reason === null) return;
    try {
      await axios.post(`${API}/content-auditor/flag`,
        { question_id, reason: reason || '(no reason given)' }, authCfg());
      toast.success('Flagged');
      await runScan();
    } catch (e) {
      toast.error(`Flag failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const doRollback = async (manifest_id) => {
    const pw = prompt2('Super Admin password to rollback:');
    if (!pw) return;
    try {
      const { data } = await axios.post(`${API}/content-auditor/rollback`,
        { manifest_id, password: pw }, authCfg());
      toast.success(`Rolled back ${data.reverted} item(s)`);
      await Promise.all([runScan(), loadHistory()]);
    } catch (e) {
      toast.error(`Rollback failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const doRegenPressPdfs = async () => {
    // v6.32.138 — force_regen_all toggle exposed to UI. Default behavior
    // (resume mode) is preserved when the checkbox is unchecked, matching
    // every prior release. Confirmation copy adapts to the chosen mode so
    // the operator can never accidentally trigger a 7-min overwrite.
    const mode = forceRegenAll
      ? 'FORCE OVERWRITE all 112 BLM press PDFs in GridFS (even if already baked). This will take ~7-10 minutes and will produce fresh PDFs carrying the v6.32.136+ parity watermark. Continue?'
      : 'Kick off press-PDF regeneration in RESUME mode (only missing files). This takes ~2 minutes. Continue?';
    if (!window.confirm(mode)) return;
    setPressRegenStarting(true);
    try {
      await axios.post(
        `${API}/content-auditor/regen-press-pdfs`,
        forceRegenAll ? { force_regen_all: true } : {},
        authCfg(),
      );
      toast.success(
        forceRegenAll
          ? 'Press PDF force-regen started — ALL 112 will be re-baked. Poll status below.'
          : 'Press PDF regeneration started — poll status below'
      );
      await loadPressStatus();
    } catch (e) {
      toast.error(`Regen failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPressRegenStarting(false);
    }
  };

  // Browser-safe prompt() wrapper (some environments block; falls back to window.prompt)
  function prompt2(message) {
    // eslint-disable-next-line no-alert
    return window.prompt(message) || '';
  }

  const stats = scan?.stats || { total_questions: 0, with_cultural_trigger: 0, with_stock_image: 0, flagged_for_review: 0 };
  const items = scan?.items || [];
  // Group items in the view by trigger for easier scanning
  const groupedByTrigger = items.reduce((acc, it) => {
    const k = (it.trigger || '?').toLowerCase();
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="content-auditor-tab">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={20} className="text-[#002FA7]" />
              <h2 className="text-xl font-bold">Content Authenticity Auditor</h2>
            </div>
            <p className="text-sm text-gray-600 max-w-2xl">
              Scans the question bank for culturally-specific prompts paired with generic stock images. Approved replacements are generated via MOSAIC image engine, held in a preview folder, and committed only after Super Admin password re-auth. Rollback manifests preserve a single-click revert.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Watching <strong>{termCount}</strong> cultural terms · auto-flags new/edited items that need review.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              asChild
              data-testid="cai-printable-guide-btn"
            >
              <a
                href="/api/docs/MOSAIC_Content_Auditor_Guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileDown size={14} className="mr-1" />
                Printable Guide (PDF)
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={runScan}
              disabled={scanning}
              data-testid="cai-scan-btn"
            >
              {scanning ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Scan size={14} className="mr-1" />}
              Scan Now
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={doRegenPressPdfs}
              disabled={pressRegenStarting}
              data-testid="cai-regen-press-btn"
              className={forceRegenAll ? 'border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100' : ''}
            >
              <Printer size={14} className="mr-1" />
              {pressRegenStarting
                ? 'Starting...'
                : (forceRegenAll ? 'Force-Regen All 112 PDFs' : 'Regenerate Press PDFs')}
            </Button>
            <label
              className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none ml-1 px-2 py-1 rounded border border-gray-200 hover:bg-amber-50"
              data-testid="cai-force-regen-all-label"
              title="When checked, sends force_regen_all=true so existing GridFS PDFs are OVERWRITTEN with fresh bakes (needed to apply the v6.32.136 parity watermark to PDFs baked before that release)."
            >
              <input
                type="checkbox"
                checked={forceRegenAll}
                onChange={(e) => setForceRegenAll(e.target.checked)}
                disabled={pressRegenStarting}
                data-testid="cai-force-regen-all-checkbox"
                className="cursor-pointer"
              />
              <span>Force overwrite all</span>
            </label>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: 'Total questions', v: stats.total_questions, color: 'text-gray-900' },
          { k: 'With cultural trigger', v: stats.with_cultural_trigger, color: 'text-blue-700' },
          { k: 'With stock image', v: stats.with_stock_image, color: 'text-amber-700' },
          { k: 'Flagged for review', v: stats.flagged_for_review, color: 'text-rose-700' },
        ].map((s) => (
          <div key={s.k} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{s.k}</div>
            <div className={`text-2xl font-semibold mt-1 ${s.color}`}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Flagged list */}
      {loading ? (
        <div className="bg-white border rounded-xl p-8 text-center text-gray-500">
          <Loader2 className="inline-block animate-spin mr-2" size={18} /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center">
          <CheckCircle2 className="mx-auto text-emerald-500 mb-2" size={32} />
          <div className="font-semibold text-emerald-800">No flagged items</div>
          <div className="text-sm text-gray-600">
            The question bank is clean. Any new culturally-specific prompt paired with a generic stock image will appear here automatically.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByTrigger).map(([trigger, group]) => (
            <div key={trigger} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-amber-600" />
                  <span className="font-semibold capitalize">{trigger}</span>
                  <Badge variant="outline">{group.length} items</Badge>
                </div>
              </div>
              <div className="divide-y">
                {group.map((it) => (
                  <div key={it.id} className="px-5 py-3 flex items-start gap-4 flex-wrap md:flex-nowrap" data-testid={`cai-item-${it.id}`}>
                    <div className="w-20 h-16 bg-gray-100 border rounded overflow-hidden flex-shrink-0">
                      {it.current_url ? (
                        <img src={it.current_url} alt="current" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">no img</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-1">
                        <code>{it.id.slice(0, 8)}</code> · Gr {it.grade_level} · {it.modality} · L{it.wida_level || '-'}
                        {it.manual_flag && <Badge className="ml-2 bg-purple-100 text-purple-700 border-purple-200" variant="outline">manual</Badge>}
                      </div>
                      <div className="text-sm font-medium truncate">{it.question_text || '(no prompt)'}</div>
                      <div className="text-xs text-gray-500 truncate">{it.big_picture_context || it.stimulus?.slice(0, 120)}</div>
                    </div>
                    <Badge className={`${riskColor(it.risk)} border flex-shrink-0`} variant="outline">{it.risk}</Badge>
                    <Button size="sm" variant="outline" onClick={() => beginGenerate(it)} data-testid={`cai-gen-${it.id}`}>
                      <Wand2 size={13} className="mr-1" /> Generate
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rollback history */}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b bg-gray-50">
            <History size={16} className="text-gray-600" />
            <span className="font-semibold">Approval History</span>
            <Badge variant="outline">{history.length}</Badge>
          </div>
          <div className="divide-y">
            {history.slice(0, 20).map((m) => (
              <div key={m.manifest_id} className="px-5 py-3 flex items-center gap-3 flex-wrap">
                <code className="text-xs text-gray-500">{m.manifest_id}</code>
                <span className="text-sm">{m.scene_key}</span>
                <Badge variant="outline">{m.question_count} items</Badge>
                <span className="text-xs text-gray-500">{m.at?.slice(0, 19).replace('T', ' ')}</span>
                <span className="text-xs text-gray-500">by {m.by}</span>
                {m.reverted_at ? (
                  <Badge className="bg-gray-100 text-gray-700 border-gray-200" variant="outline">reverted</Badge>
                ) : (
                  <Button size="sm" variant="outline" className="ml-auto" onClick={() => doRollback(m.manifest_id)} data-testid={`cai-rollback-${m.manifest_id}`}>
                    <RefreshCw size={13} className="mr-1" /> Rollback
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Press PDF status */}
      {pressStatus && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs">
          <div className="flex items-center gap-2 mb-1">
            <Printer size={14} className="text-gray-600" />
            <span className="font-semibold">Press PDF regen status</span>
            {pressStatus.running && <Badge className="bg-blue-100 text-blue-700 border-blue-200" variant="outline">running</Badge>}
            <Button size="sm" variant="ghost" onClick={loadPressStatus}><RefreshCw size={12} /></Button>
          </div>
          <div className="text-gray-500">PDFs on disk: <strong>{pressStatus.pdf_count}</strong></div>
          {pressStatus.lines?.length > 0 && (
            <pre className="mt-2 bg-gray-50 border rounded p-2 max-h-40 overflow-auto text-[11px]">
{pressStatus.lines.join('\n')}
            </pre>
          )}
        </div>
      )}

      {/* Generate + Approve dialog */}
      <Dialog open={!!activeItem} onOpenChange={(v) => !v && setActiveItem(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cai-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate & Approve Cultural Image</DialogTitle>
            <DialogDescription>
              A candidate image is generated via MOSAIC image engine. Nothing is committed until you re-enter your Super Admin password.
            </DialogDescription>
          </DialogHeader>
          {activeItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 border rounded p-3 text-xs">
                <div><strong>Item:</strong> <code>{activeItem.id}</code></div>
                <div><strong>Grade / Modality:</strong> {activeItem.grade_level} · {activeItem.modality} · L{activeItem.wida_level || '-'}</div>
                <div><strong>Question:</strong> {activeItem.question_text}</div>
                {activeItem.stimulus && <div className="mt-1"><strong>Stimulus:</strong> {activeItem.stimulus}</div>}
                <div className="mt-1"><strong>Risk:</strong> <span className={`px-2 py-0.5 rounded ${riskColor(activeItem.risk)}`}>{activeItem.risk}</span></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Scene key</Label>
                  <Input value={sceneKey} onChange={(e) => setSceneKey(e.target.value)} data-testid="cai-scene-key" />
                </div>
                <div>
                  <Label className="text-xs">Scene label (display)</Label>
                  <Input value={sceneLabel} onChange={(e) => setSceneLabel(e.target.value)} data-testid="cai-scene-label" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Prompt (edit freely)</Label>
                <Textarea
                  rows={10}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="font-mono text-xs"
                  data-testid="cai-prompt"
                />
              </div>
              <Button onClick={doGenerate} disabled={generating} data-testid="cai-generate-btn">
                {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
                {generating ? 'Generating…' : candidate ? 'Regenerate' : 'Generate Candidate Image'}
              </Button>

              {candidate && (
                <div className="space-y-3">
                  <div className="border-t pt-3">
                    <Label className="text-xs mb-2 block">Candidate preview</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] text-gray-500 mb-1">Current</div>
                        <img src={activeItem.current_url} alt="current" className="w-full h-56 object-cover border rounded" />
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500 mb-1">Proposed (MOSAIC image engine)</div>
                        <img src={candidate.candidate_url} alt="proposed" className="w-full h-56 object-cover border rounded" />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-3 space-y-2">
                    <Label className="text-xs">Super Admin password (required to commit)</Label>
                    <Input
                      type="password"
                      value={approvePw}
                      onChange={(e) => setApprovePw(e.target.value)}
                      placeholder="Your Super Admin password"
                      data-testid="cai-approve-pw"
                    />
                    <div className="text-[11px] text-gray-500">
                      This approval updates <strong>{activeItem.manual_flag ? 1 : 'all matching'}</strong> item(s) and writes a rollback manifest. Digital surfaces pick up the change instantly; press PDFs regenerate via the button at the top.
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => doApprove([activeItem.id])} disabled={approving} data-testid="cai-approve-item">
                        {approving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
                        Approve (this item only)
                      </Button>
                      {groupedByTrigger[(activeItem.trigger || '').toLowerCase()]?.length > 1 && (
                        <Button
                          variant="outline"
                          onClick={() => doApprove(groupedByTrigger[(activeItem.trigger || '').toLowerCase()].map((x) => x.id))}
                          disabled={approving}
                          data-testid="cai-approve-all-trigger"
                        >
                          Apply to all {groupedByTrigger[(activeItem.trigger || '').toLowerCase()].length} “{activeItem.trigger}” items
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActiveItem(null)} data-testid="cai-cancel">
              <X size={14} className="mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentAuthenticityAuditor;
